/* Nahshon Jeremiah Arts — motion + interactions
   GSAP/ScrollTrigger drive everything scroll-linked; Lenis smooths the wheel. */
(() => {
  const root = document.documentElement;
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasGsap = !!window.gsap && !!window.ScrollTrigger;

  // Page transitions are native cross-document view transitions (see site.css):
  // navigation starts instantly, the browser animates between the two pages.
  if (!location.hash) scrollTo(0, 0);

  /* ---------- header + menu ---------- */
  const header = document.querySelector(".site-header");
  const toggle = document.querySelector(".menu-toggle");
  toggle && toggle.addEventListener("click", () => {
    const open = document.body.classList.toggle("menu-open");
    toggle.setAttribute("aria-expanded", open);
    window.__lenis && (open ? __lenis.stop() : __lenis.start());
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.body.classList.contains("menu-open")) {
      document.body.classList.remove("menu-open");
      toggle && toggle.setAttribute("aria-expanded", "false");
      toggle && toggle.focus();
    }
  });

  let lastY = scrollY;
  function onScrollChrome(y) {
    if (!header) return;
    // stay transparent over the painted hero, go solid once the page proper starts
    const scene = document.querySelector(".scene");
    header.classList.toggle("is-solid", y > (scene ? scene.offsetHeight - innerHeight * 0.9 : 40));
    const hide = y > lastY && y > 400 && !document.body.classList.contains("menu-open");
    header.classList.toggle("is-hidden", hide);
    lastY = y;
  }

  /* ---------- gallery filters (works without GSAP) ---------- */
  document.querySelectorAll("[data-filter-group]").forEach((group) => {
    const grid = document.querySelector(group.dataset.filterGroup);
    group.addEventListener("click", (e) => {
      const b = e.target.closest("button[data-filter]");
      if (!b) return;
      group.querySelectorAll("button").forEach((x) => x.setAttribute("aria-pressed", x === b));
      const f = b.dataset.filter;
      grid.querySelectorAll("figure").forEach((fig) => {
        fig.hidden = f !== "all" && fig.dataset.cat !== f;
      });
      hasGsap && ScrollTrigger.refresh();
    });
  });

  /* ---------- quote form: Shopify emails it; WhatsApp is the alternative ---------- */
  document.querySelectorAll("form[data-quote]").forEach((form) => {
    const select = form.querySelector("[data-service]");
    const preset = new URLSearchParams(location.search).get("service");
    if (preset && select) {
      const match = [...select.options].find((o) => o.value.toLowerCase() === preset.toLowerCase());
      if (match) select.value = match.value;
    }
    form.addEventListener("submit", (e) => { if (!form.reportValidity()) e.preventDefault(); });
    const wa = form.querySelector("[data-wa-send]");
    wa && wa.addEventListener("click", () => {
      const v = (n) => (form.elements[n] ? form.elements[n].value.trim() : "");
      if (!v("contact[name]") || !v("contact[body]")) { form.reportValidity(); return; }
      const lines = [
        "Hi Nahshon Jeremiah Arts, I'd like a quote.",
        `Name: ${v("contact[name]")}`,
        `Service: ${v("contact[Service]")}`,
        v("contact[Event date]") ? `Date: ${v("contact[Event date]")}` : "",
        v("contact[phone]") ? `Phone: ${v("contact[phone]")}` : "",
        `Details: ${v("contact[body]")}`,
      ].filter(Boolean);
      const num = (window.NJA && NJA.whatsapp) || "14735342669";
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
    });
  });

  /* ---------- option picker: looping 3D cover-flow; the centre card is the selected one ---------- */
  document.querySelectorAll("[data-picker]").forEach((picker) => {
    const rail = picker.querySelector("[data-picker-rail]");
    const cards = [...picker.querySelectorAll("[data-picker-card]")];
    const panels = [...picker.querySelectorAll(".pdetail")];
    const dots = [...picker.querySelectorAll("[data-picker-dot]")];
    const prev = picker.querySelector("[data-picker-prev]");
    const next = picker.querySelector("[data-picker-next]");
    const n = cards.length;
    if (!rail || !n) return;
    picker.classList.add("picker--3d");          // already in the markup; kept for older cached pages

    const mod = (x) => ((x % n) + n) % n;
    const around = (o) => mod(o + n / 2) - n / 2;           // shortest way round the loop
    let pos = 0, target = 0, cur = -1, raf = 0, last = 0, step = 1;

    const set = (i) => {
      if (i === cur) return;
      cur = i;
      cards.forEach((c, k) => {
        const on = k === i;
        c.classList.toggle("is-active", on); c.setAttribute("aria-selected", on); c.tabIndex = on ? 0 : -1;
        const cta = c.querySelector(".pcard__cta"); if (cta) cta.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p, k) => { p.classList.toggle("is-active", k === i); p.inert = k !== i; });
      dots.forEach((d, k) => d.classList.toggle("is-active", k === i));
    };

    // every card is placed by its distance from centre (in cards), evenly spaced so no card
    // ever slides over another: the incoming one is fully in view the whole way in. Side cards
    // turn gently away, step back and wash into the page; far ones fade before they wrap.
    const measure = () => { step = cards[0].offsetWidth * 1.12; };
    const render = () => {
      cards.forEach((c, i) => {
        const o = around(i - pos), a = Math.abs(o), sgn = Math.sign(o);
        const x = o * step;
        const z = -Math.min(a, 1) * 90;
        const rot = -sgn * Math.min(a, 1) * 9;
        c.style.transform = `translate3d(${x.toFixed(1)}px,0,${z.toFixed(1)}px) rotateY(${rot.toFixed(2)}deg)`;
        c.style.opacity = a < 1.6 ? "1" : Math.max(0, 1 - (a - 1.6) * 1.1).toFixed(3);
        c.style.zIndex = String(100 - Math.round(a * 10));
        c.classList.toggle("is-far", a > 2.85);
        c.firstElementChild.style.setProperty("--dim", Math.min(a * 0.38, 0.5).toFixed(3));
      });
    };

    // glide: an exponential ease toward the target, frame-rate independent
    const tick = (t) => {
      const dt = Math.min(64, t - (last || t)); last = t;
      if (!dragging) pos += (target - pos) * (reduce ? 1 : 1 - Math.pow(1 - 0.14, dt / 16.67));
      if (Math.abs(target - pos) < 0.0005 && !dragging) { pos = target; render(); raf = 0; last = 0; return; }
      render();
      raf = requestAnimationFrame(tick);
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(tick); };
    const aim = (t) => { target = t; set(mod(Math.round(t))); kick(); };
    const go = (i) => aim(target + around(i - mod(Math.round(target))));

    // drag / swipe with momentum (vertical swipes still scroll the page)
    let dragging = false, down = null, moved = false;
    rail.addEventListener("pointerdown", (e) => {
      if (e.button !== 0 || e.target.closest(".pcard__cta")) return;
      down = { x: e.clientX, y: e.clientY, t0: target, vx: 0, lx: e.clientX, lt: e.timeStamp };
      moved = false;
    });
    rail.addEventListener("pointermove", (e) => {
      if (!down) return;
      const dx = e.clientX - down.x;
      if (!dragging) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(e.clientY - down.y)) return;
        dragging = moved = true; rail.classList.add("is-dragging");
        try { rail.setPointerCapture(e.pointerId); } catch (_) {}
        down.x = e.clientX; down.t0 = pos;
      }
      const dtm = Math.max(1, e.timeStamp - down.lt);
      down.vx = 0.8 * ((e.clientX - down.lx) / dtm) + 0.2 * down.vx;
      down.lx = e.clientX; down.lt = e.timeStamp;
      pos = target = down.t0 - (e.clientX - down.x) / step;
      set(mod(Math.round(pos))); kick();
    });
    const release = () => {
      if (!down) return;
      if (dragging) {
        const fresh = performance.now() - down.lt < 90;      // a held-still release has no fling
        const fling = fresh ? Math.max(-1.2, Math.min(1.2, -down.vx * 150 / step)) : 0;
        dragging = false; rail.classList.remove("is-dragging");
        aim(Math.round(pos + fling));
      }
      down = null;
    };
    rail.addEventListener("pointerup", release);
    rail.addEventListener("pointercancel", release);
    rail.addEventListener("lostpointercapture", release);

    // trackpads: sideways scroll turns it, then it settles on the nearest card
    let wheelEnd = 0;
    rail.addEventListener("wheel", (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      target += e.deltaX / (step * 1.4); set(mod(Math.round(target))); kick();
      clearTimeout(wheelEnd); wheelEnd = setTimeout(() => aim(Math.round(target)), 140);
    }, { passive: false });

    // tap a side card to bring it forward; tap the front card anywhere to follow its button
    cards.forEach((c, i) => {
      c.addEventListener("click", (e) => {
        if (moved) { e.preventDefault(); moved = false; return; }
        const cta = c.querySelector(".pcard__cta");
        if (i === cur && cta && !e.target.closest(".pcard__cta")) cta.click();
        else if (i !== cur) go(i);
      });
      c.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); c.click(); } });
    });
    dots.forEach((d, i) => d.addEventListener("click", () => go(i)));
    prev && prev.addEventListener("click", () => aim(Math.round(target) - 1));
    next && next.addEventListener("click", () => aim(Math.round(target) + 1));
    rail.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); aim(Math.round(target) + 1); cards[cur].focus({ preventScroll: true }); }
      if (e.key === "ArrowLeft") { e.preventDefault(); aim(Math.round(target) - 1); cards[cur].focus({ preventScroll: true }); }
    });

    // links like /pages/art-services#murals open on that option
    const want = location.hash.slice(1);
    const start = want ? cards.findIndex((c) => c.dataset.anchor === want) : -1;
    pos = target = Math.max(0, start);
    measure(); set(mod(pos)); render();
    requestAnimationFrame(() => requestAnimationFrame(() => picker.classList.add("picker--ready")));
    addEventListener("resize", () => { measure(); render(); });
    if (start > 0) requestAnimationFrame(() => picker.scrollIntoView());
  });

  /* ---------- service page: option price, and the phone buy bar while the card is out of view ---------- */
  document.querySelectorAll("[data-svc]").forEach((svc) => {
    const prices = svc.querySelectorAll("[data-price]");
    svc.querySelectorAll('input[name="id"][data-money]').forEach((r) =>
      r.addEventListener("change", () => prices.forEach((p) => (p.textContent = r.dataset.money))));
    const card = svc.querySelector(".svc__card"), bar = svc.querySelector("[data-svc-bar]");
    if (!card || !bar || !("IntersectionObserver" in window)) return;
    new IntersectionObserver(([e]) => {
      const on = !e.isIntersecting;
      bar.classList.toggle("is-on", on); bar.setAttribute("aria-hidden", !on);
      bar.querySelector("a").tabIndex = on ? 0 : -1;
    }).observe(card);
    bar.querySelector("a").addEventListener("click", (e) => {
      e.preventDefault();
      const y = card.getBoundingClientRect().top + scrollY - 90;
      window.__lenis ? window.__lenis.scrollTo(y) : scrollTo({ top: y, behavior: reduce ? "auto" : "smooth" });
    });
  });

  /* ---------- Journal: topic chips filter More Stories in place ---------- */
  document.querySelectorAll("[data-journal]").forEach((j) => {
    const chips = j.querySelectorAll(".j-chip"), cards = j.querySelectorAll(".journal__grid .j-card");
    chips.forEach((c) => c.addEventListener("click", () => {
      chips.forEach((x) => { x.classList.toggle("is-on", x === c); x.setAttribute("aria-pressed", x === c); });
      const t = c.dataset.topic;
      cards.forEach((card) => { card.hidden = !!t && card.dataset.topic !== t; });
    }));
  });

  /* ---------- Art Services mural: its gentle SVG motion rests for reduced-motion users ---------- */
  if (reduce) document.querySelectorAll("svg.mural").forEach((m) => m.pauseAnimations && m.pauseAnimations());

  if (!hasGsap) { root.classList.remove("nja-intro"); addEventListener("scroll", () => onScrollChrome(scrollY), { passive: true }); return; }

  gsap.registerPlugin(ScrollTrigger);

  /* ---------- smooth scroll ---------- */
  let lenis = null;
  if (!reduce && window.Lenis) {
    lenis = new Lenis({ lerp: 0.14, wheelMultiplier: 1 });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    document.querySelectorAll('a[href^="#"]').forEach((a) => a.addEventListener("click", (e) => {
      const t = document.querySelector(a.getAttribute("href"));
      if (t) { e.preventDefault(); lenis.scrollTo(t, { offset: -80 }); }
    }));
  }

  const progress = document.querySelector(".progress i");
  ScrollTrigger.create({
    start: 0, end: "max",
    onUpdate: (self) => {
      onScrollChrome(self.scroll());
      if (progress) progress.style.transform = `scaleX(${self.progress})`;
    },
  });

  if (reduce) { root.classList.remove("nja-intro"); document.querySelectorAll("svg.live").forEach((svg) => svg.pauseAnimations()); return; }

  const mm = gsap.matchMedia();

  /* ---------- home hero: painted Grenada in depth ---------- */
  const scene = document.querySelector(".scene");
  if (scene) {
    const q = (s) => scene.querySelector(s);
    const L = { mark: q("[data-l=mark] > div"), sky: q("[data-l=sky]"), sun: q("[data-l=sun]"), far: q("[data-l=far]"), town: q("[data-l=town]"), sea: q("[data-l=sea]"), fg: q("[data-l=fg]"), canopy: q("[data-l=canopy]"), dots: q("[data-l=dots]") };
    const title = q(".scene__title");
    const rows = scene.querySelectorAll(".scene__title .row > span");
    const sub = q(".scene__title p");

    // load: camera dollies in fast, headline cuts up.
    // Intro moves the inner <svg>s so it never fights the scroll tweens on the .layer wrappers.
    const inner = (el) => el.firstElementChild;
    const intro = gsap.timeline({ delay: 0.1 });
    intro
      .from([L.far, L.town, L.sea].map(inner), { scale: 1.12, yPercent: 6, duration: 1.1, ease: "expo.out", stagger: 0.06 }, 0)
      .from(inner(L.fg), { scale: 1.35, duration: 1.2, ease: "expo.out" }, 0)
      .from(inner(L.canopy), { yPercent: -30, duration: 1.2, ease: "expo.out" }, 0)
      .from(inner(L.sun), { yPercent: 18, duration: 1.2, ease: "expo.out" }, 0)
      .from(rows, { yPercent: 110, duration: 0.8, ease: "power4.out", stagger: 0.08 }, 0.2);
    if (sub) intro.from(sub, { opacity: 0, y: 16, duration: 0.6, ease: "power3.out" }, 0.55);

    // scroll: the camera cranes up out of the Carenage into the evening sky.
    // Nearer planes sink faster, opening the sky for the logo, which rises clear
    // of the hills and holds while the plants close back in around it.
    const vh = (k) => () => innerHeight * k;
    const motto = q(".scene__mark p");
    const tl = gsap.timeline({ defaults: { ease: "none" }, scrollTrigger: { trigger: scene, start: "top top", end: "bottom bottom", scrub: 0.5, invalidateOnRefresh: true } });
    tl.to(title, { y: vh(-0.35), opacity: 0, duration: 0.22, ease: "power1.in" }, 0)
      .to(q(".scene__cue"), { opacity: 0, duration: 0.04 }, 0)
      .to(L.sky, { y: vh(0.04), duration: 0.6 }, 0.05)
      .to(L.sun, { y: vh(0.04), scale: 1.08, duration: 0.6, ease: "power1.inOut" }, 0.05)
      .to(L.far, { y: vh(0.34), duration: 0.55, ease: "power1.inOut" }, 0.05)
      .to(L.town, { y: vh(0.5), duration: 0.55, ease: "power1.inOut" }, 0.05)
      .to(L.sea, { y: vh(0.56), duration: 0.55, ease: "power1.inOut" }, 0.05)   // close to the town plane, so the shoreline never splits
      .to(L.dots, { y: vh(0.3), duration: 0.6 }, 0.05)
      .to(L.fg, { y: vh(0.8), scale: 1.25, duration: 0.5, ease: "power2.in" }, 0.05)
      .to(L.canopy, { y: vh(-0.55), duration: 0.45, ease: "power2.in" }, 0.05)   // palm lifts clear of the sky
      .fromTo(L.mark, { y: vh(0.8), opacity: 1 }, { y: 0, duration: 0.5, ease: "power2.out" }, 0.1)
      .fromTo(motto, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.12, ease: "power2.out" }, 0.48)
      .to(L.mark, { scale: 1.04, duration: 0.4 }, 0.6)   // the hold: fully visible, gently breathing
      // the foreground plants swing back in to frame the logo, then hand off to the next section
      .to(L.fg, { y: vh(0.22), scale: 1.12, duration: 0.3, ease: "power2.out" }, 0.7)
      .to(L.canopy, { y: vh(-0.14), duration: 0.3, ease: "power2.out" }, 0.7);

    // living details (birds, boats, glitter, leaves) only run while the hero is on screen
    const live = [...scene.querySelectorAll("svg.live")];
    ScrollTrigger.create({ trigger: scene, start: "top bottom", end: "bottom top",
      onToggle: (self) => {
        scene.classList.toggle("is-offscreen", !self.isActive);
        live.forEach((svg) => (self.isActive ? svg.unpauseAnimations() : svg.pauseAnimations()));
      } });

    // pointer adds a little extra depth on desktop
    mm.add("(pointer: fine)", () => {
      const layers = [[L.mark, 3], [L.far, 6], [L.town, 12], [L.sea, 18], [L.fg, 34], [L.canopy, 30], [L.dots, 26]];
      const setters = layers.map(([el, d]) => [gsap.quickTo(el, "x", { duration: 0.8, ease: "power3" }), d]);
      const move = (e) => {
        const nx = e.clientX / innerWidth - 0.5;
        setters.forEach(([set, d]) => set(-nx * d));
      };
      addEventListener("pointermove", move);
      return () => removeEventListener("pointermove", move);
    });
  }

  /* ---------- home doorway bands ----------
     Each band is its own little 2.5D window: far layers drift, near ones travel, and the
     title settles in as the band arrives. Short, quick moves only. */
  const LAYER_DEPTH = { sky: 0.03, sun: 0.06, far: 0.1, town: 0.14, sea: 0.18, dots: 0.22, fg: 0.26, canopy: 0.2 };
  gsap.utils.toArray(".band").forEach((band, i) => {
    band.querySelectorAll(".layer").forEach((layer) => {
      const d = parseFloat(layer.dataset.depth) || LAYER_DEPTH[layer.dataset.l] || 0.1;
      gsap.fromTo(layer, { y: () => d * 170 }, {
        y: () => -d * 170, ease: "none",
        scrollTrigger: { trigger: band, start: "top bottom", end: "bottom top", scrub: true, invalidateOnRefresh: true },
      });
    });
    const text = band.querySelector(".band__text");
    gsap.fromTo(text, { y: -18 }, { y: 30, ease: "none",
      scrollTrigger: { trigger: band, start: "top bottom", end: "bottom top", scrub: true } });
    // idle breathing: mid and near layers drift slowly side to side, out of step, so the
    // scene feels alive before anyone scrolls (x only, so it never fights the scroll y)
    band.querySelectorAll(".layer").forEach((layer, k) => {
      const d = parseFloat(layer.dataset.depth) || LAYER_DEPTH[layer.dataset.l] || 0.1;
      if (d < 0.12) return;
      gsap.to(layer, { x: (k % 2 ? -1 : 1) * d * 34, duration: 5 + k * 0.9, ease: "sine.inOut", yoyo: true, repeat: -1, delay: -k });
    });
    const title = band.querySelector(".band__title > span");
    const rest = band.querySelectorAll(".band__kicker, .band__tagline, .band__go");

    // tap: lean into the chosen scene for a beat, then go (modifier clicks open normally)
    band.addEventListener("click", (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || !band.href) return;
      e.preventDefault();
      band.closest(".bands").classList.add("is-leaving");
      band.classList.add("is-chosen");
      gsap.to(band.querySelectorAll(".layer > svg"), { scale: 1.09, duration: 0.42, ease: "power2.in", stagger: 0.02 });
      gsap.to(text, { y: -24, opacity: 0, duration: 0.3, ease: "power2.in" });
      setTimeout(() => { location.href = band.href; }, 380);
    });
    if (i === 0) {
      // fromTo: start exactly where the CSS first frame already holds them (.nja-intro);
      // y: 0 stops GSAP keeping that CSS offset as an extra pixel shift
      gsap.timeline({ delay: 0.1 })
        .fromTo(band.querySelectorAll(".layer > svg"), { scale: 1.08, yPercent: 3, y: 0 }, { scale: 1, yPercent: 0, y: 0, duration: 1.1, ease: "expo.out", stagger: 0.03 }, 0)
        .fromTo(title, { yPercent: 105, y: 0 }, { yPercent: 0, y: 0, duration: 0.8, ease: "power4.out" }, 0.2)
        .fromTo(rest, { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: "power3.out", stagger: 0.07 }, 0.45);
    } else {
      gsap.timeline({ scrollTrigger: { trigger: band, start: "top 78%", toggleActions: "play none none reverse" } })
        .fromTo(title, { yPercent: 105, y: 0 }, { yPercent: 0, y: 0, duration: 0.7, ease: "power4.out" })
        .fromTo(rest, { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "power3.out", stagger: 0.06 }, 0.15);
    }
  });

  // desktop: the pointer adds a touch of depth to whichever door it's over
  mm.add("(pointer: fine)", () => {
    const sets = gsap.utils.toArray(".band .layer").map((layer) => {
      const d = parseFloat(layer.dataset.depth) || LAYER_DEPTH[layer.dataset.l] || 0.1;
      const svg = layer.firstElementChild;
      return [gsap.quickTo(svg, "x", { duration: 0.9, ease: "power3" }), gsap.quickTo(svg, "y", { duration: 0.9, ease: "power3" }), d];
    });
    if (!sets.length) return;
    const move = (e) => {
      const nx = e.clientX / innerWidth - 0.5, ny = e.clientY / innerHeight - 0.5;
      sets.forEach(([sx, sy, d]) => { sx(-nx * d * 90); sy(-ny * d * 40); });
    };
    addEventListener("pointermove", move);
    return () => removeEventListener("pointermove", move);
  });

  /* ---------- generic depth layers ---------- */
  gsap.utils.toArray("[data-depth]").filter((el) => !el.closest(".band")).forEach((el) => {
    const d = parseFloat(el.dataset.depth) || 0.2;
    gsap.fromTo(el, { y: () => d * 220 }, {
      y: () => -d * 220, ease: "none",
      scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true, invalidateOnRefresh: true },
    });
  });
  gsap.utils.toArray("[data-spin]").forEach((el) => {
    gsap.to(el, { rotate: parseFloat(el.dataset.spin), ease: "none",
      scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true } });
  });

  /* image moves inside its painted frame */
  gsap.utils.toArray(".brushframe img").forEach((img) => {
    gsap.fromTo(img, { yPercent: -8 }, { yPercent: 8, ease: "none",
      scrollTrigger: { trigger: img.parentElement, start: "top bottom", end: "bottom top", scrub: true } });
  });

  /* brush underline paints itself across as the heading crosses the viewport */
  gsap.utils.toArray(".stroke-word path").forEach((p) => {
    gsap.fromTo(p, { strokeDasharray: 1, strokeDashoffset: 1 }, { strokeDashoffset: 0, ease: "none",
      scrollTrigger: { trigger: p.closest(".stroke-word"), start: "top 88%", end: "top 55%", scrub: 0.4 } });
  });

  /* ---------- home: stacked panels settle back as the next one slides over ----------
     Scale + a plum veil only (no tilt, no transparency, so the cards beneath never show
     through). The move spans exactly the distance the next card travels to its resting
     spot, so it finishes as the card lands. */
  mm.add("(min-width: 801px)", () => {
    const panels = gsap.utils.toArray(".panel");
    panels.forEach((panel, i) => {
      const next = panels[i + 1];
      if (!next) return;
      gsap.fromTo(panel, { scale: 1, "--veil": 0 }, {
        scale: 0.94, "--veil": 0.28, ease: "none",
        scrollTrigger: {
          trigger: next, start: "top bottom",
          end: () => `top ${parseFloat(getComputedStyle(next).top) || 0}px`,
          scrub: 0.4, invalidateOnRefresh: true,
        },
      });
    });
  });

  /* ---------- home: horizontal reel of paintings ---------- */
  mm.add("(min-width: 701px)", () => {
    const reel = document.querySelector(".reel");
    if (!reel) return;
    const track = reel.querySelector(".reel__track");
    const bar = reel.querySelector(".reel__progress i");
    const dist = () => track.scrollWidth - innerWidth;
    const move = gsap.to(track, { x: () => -dist(), ease: "none",
      scrollTrigger: { trigger: reel, start: "top top", end: "bottom bottom", scrub: 0.5, invalidateOnRefresh: true,
        onUpdate: (s) => bar && (bar.style.transform = `scaleX(${s.progress})`) } });
    // the plants sway the other way, so the reel feels like it's passing through foliage
    gsap.fromTo(reel.querySelector(".reel__plant--top"), { x: 0, rotate: 0 }, { x: () => innerWidth * 0.06, rotate: -6, ease: "none",
      scrollTrigger: { trigger: reel, start: "top bottom", end: "bottom top", scrub: 0.6 } });
    gsap.fromTo(reel.querySelector(".reel__plant--bottom"), { x: 0, y: 60 }, { x: () => -innerWidth * 0.04, y: -40, ease: "none",
      scrollTrigger: { trigger: reel, start: "top bottom", end: "bottom top", scrub: 0.6 } });
    reel.querySelectorAll(".reel__item").forEach((item, i) => {
      gsap.fromTo(item, { y: i % 2 ? 60 : -40 }, { y: i % 2 ? -60 : 40, ease: "none",
        scrollTrigger: { trigger: item, containerAnimation: move, start: "left right", end: "right left", scrub: true } });
    });
  });

  /* ---------- reviews drift in opposite directions ---------- */
  gsap.utils.toArray(".drift").forEach((row, i) => {
    gsap.fromTo(row, { xPercent: i ? -28 : 0 }, { xPercent: i ? 0 : -28, ease: "none",
      scrollTrigger: { trigger: row.closest("section"), start: "top bottom", end: "bottom top", scrub: 0.6 } });
  });

  /* value pills scatter in at different depths */
  gsap.utils.toArray(".values li").forEach((li, i) => {
    gsap.from(li, { y: 60 + (i % 3) * 40, rotate: (i % 2 ? 1 : -1) * 8, opacity: 0, ease: "power2.out",
      scrollTrigger: { trigger: li.parentElement, start: "top 90%", end: "top 45%", scrub: 0.5 } });
  });

  addEventListener("load", () => ScrollTrigger.refresh());
})();
